import { useActiveCampaign, useCampaignBySlug } from "@/hooks/useActiveCampaign";
import { useCampaignPackagePurchase } from "@/hooks/useCampaignPackagePurchase";
import { useAuth } from "@/components/auth/AuthProvider";
import { useUnlimitedStatus } from "@/hooks/useUnlimitedStatus";
import { useUserSubscription } from "@/hooks/useUserSubscription";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Check, Crown, Loader2, RefreshCw } from "lucide-react";
import { Link, useSearchParams, useParams, useLocation } from "react-router-dom";
import { format } from "date-fns";
import UnlimitedStatusCard from "@/components/profile/UnlimitedStatusCard";
import OneOffPackageCard from "@/components/offers/OneOffPackageCard";
import TrialSubscriptionCard from "@/components/offers/TrialSubscriptionCard";

const Offers = () => {
  const { slug } = useParams<{ slug?: string }>();
  const location = useLocation();
  const currentPath = location.pathname + location.search;
  const { userId, isAuthenticated } = useAuth();
  const activeCampaignQuery = useActiveCampaign();
  const slugCampaignQuery = useCampaignBySlug(slug);
  
  const { data, isLoading } = slug ? slugCampaignQuery : activeCampaignQuery;
  const { purchasePackage, isLoading: isPurchasing } = useCampaignPackagePurchase();
  const { data: unlimitedStatus } = useUnlimitedStatus();
  const { data: existingSubscription } = useUserSubscription();
  const [searchParams] = useSearchParams();
  const partnerCode = searchParams.get("partnerCode");

  // Check if user has ever booked before (for first_time_only packages)
  const { data: hasBooked, isLoading: isCheckingEligibility } = useQuery({
    queryKey: ["user-has-booked", userId],
    queryFn: async () => {
      if (!userId) return false;
      const { count, error } = await supabase
        .from("tickets")
        .select("*", { count: "exact", head: true })
        .eq("user_id", userId)
        .eq("status", "confirmed");

      if (error) {
        console.error("Error checking booking history:", error);
        return false;
      }
      return (count || 0) > 0;
    },
    enabled: !!userId,
  });

  // Remaining stock per package (null = unlimited)
  const packageIds = (data?.packages ?? []).map((p) => p.id);
  const { data: availability } = useQuery({
    queryKey: ["campaign-package-availability", packageIds],
    queryFn: async () => {
      const { data: rows, error } = await (supabase as any).rpc(
        "get_campaign_package_availability",
        { _package_ids: packageIds }
      );
      if (error) throw error;
      const map: Record<string, number | null> = {};
      (rows ?? []).forEach((r: any) => { map[r.package_id] = r.remaining; });
      return map;
    },
    enabled: packageIds.length > 0,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4 text-center">
        <Sparkles className="h-12 w-12 text-muted-foreground mb-4" />
        <h1 className="text-2xl font-bold text-foreground mb-2">No Active Offers</h1>
        <p className="text-muted-foreground mb-6">
          Check back soon for exclusive deals and promotions!
        </p>
        <Link to="/events">
          <Button>Browse Events</Button>
        </Link>
      </div>
    );
  }

  const { campaign, packages } = data;
  const endDate = new Date(campaign.end_date);

  // Filter packages by partner code
  const filteredPackages = packages.filter((pkg) => {
    if (!pkg.partner_code_filter) return true; // no filter = always visible
    return partnerCode && pkg.partner_code_filter.toUpperCase() === partnerCode.toUpperCase();
  });

  const hasActiveSubscription = existingSubscription && 
    (existingSubscription.status === "trialing" || existingSubscription.status === "active");
  const currentSubscriptionPackageId = hasActiveSubscription ? existingSubscription.package_id : null;

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground p-8 text-center">
        <Sparkles className="h-10 w-10 mx-auto mb-4 text-secondary" />
        <h1 className="text-3xl font-bold mb-2">
          {campaign.hero_title || campaign.name}
        </h1>
        {campaign.hero_subtitle && (
          <p className="text-primary-foreground/80 mb-2">
            {campaign.hero_subtitle}
          </p>
        )}
        {campaign.description && (
          <p className="text-primary-foreground/70 text-sm mb-2 whitespace-pre-line max-w-md mx-auto">
            {campaign.description}
          </p>
        )}
        <p className="text-sm text-primary-foreground/70">
          Ends {format(endDate, "MMMM do")}
        </p>
      </div>

      {/* Unlimited Status Banner */}
      {unlimitedStatus?.isUnlimited && (
        <div className="p-4 max-w-lg mx-auto">
          <UnlimitedStatusCard />
          <p className="text-center text-sm text-muted-foreground mt-3">
            You already have unlimited access! Enjoy booking any experience for free.
          </p>
        </div>
      )}

      {/* Packages */}
      <div className="p-4 max-w-lg mx-auto space-y-4">
        {filteredPackages.map((pkg) => {
          if (pkg.package_type === "trial_subscription") {
            return (
              <TrialSubscriptionCard
                key={pkg.id}
                pkg={pkg}
                isAuthenticated={isAuthenticated}
                isPurchasing={isPurchasing}
                currentSubscriptionPackageId={currentSubscriptionPackageId}
                isUnlimitedUser={!!unlimitedStatus?.isUnlimited}
                currentPath={currentPath}
                onPurchase={() => purchasePackage(pkg)}
                remaining={availability?.[pkg.id] ?? null}
              />
            );
          }

          return (
            <OneOffPackageCard
              key={pkg.id}
              pkg={pkg}
              isAuthenticated={isAuthenticated}
              isPurchasing={isPurchasing}
              isUnlimitedUser={!!unlimitedStatus?.isUnlimited}
              hasBooked={hasBooked}
              isCheckingEligibility={isCheckingEligibility}
              currentPath={currentPath}
              onPurchase={() => purchasePackage(pkg)}
              remaining={availability?.[pkg.id] ?? null}
            />
          );
        })}
      </div>

      {/* Footer note */}
      <div className="text-center p-4 text-sm text-muted-foreground">
        <p>Credits never expire and can be used for any Nimi experience</p>
      </div>
    </div>
  );
};

export default Offers;
